import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useSession } from './session';
import { McperRoute } from './routes';
import { mcperService } from './mcper.service';
import { t } from './i18n.en';

/** The wordmark: the name in the display face, the ".io" in lime on dark, olive on light. */
export function Wordmark({ tone }: { tone: 'dark' | 'light' }) {
  return (
    <span className={`mio-wordmark ${tone}`} role="img" aria-label={t.name}>
      MCPer<span>.io</span>
    </span>
  );
}

/** Sidebar frame of every signed-in page: the six sections of the dashboard, the library count, the person. */
export function McperShell() {
  const navigate = useNavigate();
  const person = useSession((s) => s.person);
  const clear = useSession((s) => s.clear);
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState({ library: 0, building: 0 });

  useEffect(() => {
    let alive = true;
    const load = () => mcperService.counts().then((c) => alive && setCounts(c)).catch(() => undefined);
    void load();
    const id = window.setInterval(load, 10_000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, []);

  const signOut = async () => {
    await mcperService.logout().catch(() => undefined);
    clear();
    navigate(McperRoute.Login);
  };

  const name = person?.name || person?.email || '';
  const initials = name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <div className="mio-shell">
      {open && <div className="mio-backdrop" onClick={() => setOpen(false)} aria-hidden="true" />}
      <aside className={`mio-sidebar${open ? ' open' : ''}`}>
        <NavLink to={McperRoute.Overview} className="mio-sidebar-brand" end>
          <Wordmark tone="dark" />
        </NavLink>
        <nav className="mio-nav" onClick={() => setOpen(false)}>
          <NavLink to={McperRoute.Overview} end>{t.nav.overview}</NavLink>
          <NavLink to={McperRoute.Sites}>{t.nav.sites}</NavLink>
          <NavLink to={McperRoute.SignIns}>{t.nav.signIns}</NavLink>
          <NavLink to={McperRoute.Agents}>{t.nav.agents}</NavLink>
          <NavLink to={McperRoute.Calls}>{t.nav.calls}</NavLink>
          <NavLink to={McperRoute.Settings}>{t.nav.settings}</NavLink>
        </nav>
        <div className="mio-grow" />
        <div className="mio-sidebar-note">
          {t.nav.library}: <strong>{t.nav.sitesCount(counts.library)}</strong>
          <br />
          {t.nav.buildingCount(counts.building)}
        </div>
        <div className="mio-person">
          <span className="mio-avatar" aria-hidden="true">{initials}</span>
          <span className="mio-person-name">{name}</span>
          <button type="button" className="mio-link-btn" onClick={signOut}>{t.nav.signOut}</button>
        </div>
      </aside>
      <main className="mio-main">
        <button type="button" className="mio-menu-btn" onClick={() => setOpen(true)} aria-label={t.nav.menu}>
          <span /><span /><span />
        </button>
        <Outlet />
      </main>
    </div>
  );
}
